import { useCallback, useState } from 'react';
import { REFERRAL } from '@app/shared';
import { useAppApi } from '../../app/ApiProvider';
import { useAsync } from '../../hooks/useAsync';
import { useI18n } from '../../i18n/I18nProvider';
import { copyText, shareLink } from '../../telegram/telegram';
import { SmallButton } from '../../ui/Cta';
import { ErrorState, Loader } from '../../ui/feedback';
import { SectionTitle } from '../../ui/Screen';
import { Card, Hint, ValueRow } from './profile/fields';

/**
 * «Пригласить коллегу»: реферальная ссылка и счётчики. Паттерн — как у `ShareBlock`.
 *
 * Числа условий берутся из `REFERRAL` (@app/shared), а не вписаны в строку: бэкенд начисляет
 * по тем же константам. Потолок приходит с сервера вместе с остальным ответом.
 */
export function ReferralBlock(props: { businessId: string }): JSX.Element {
  const { t } = useI18n();
  const api = useAppApi();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const { state, reload } = useAsync(
    useCallback(() => api.cabinet.getReferral(props.businessId), [api, props.businessId]),
    [props.businessId],
  );

  const act = (fn: () => Promise<void> | void) => async () => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <div className="px-4 pt-6">
      <SectionTitle>{t('refTitle')}</SectionTitle>
      <Card>
        {state.status === 'loading' ? <Loader /> : null}
        {state.status === 'failed' ? <ErrorState error={state.error} onRetry={reload} withTitle={false} /> : null}
        {state.status === 'ready' ? (
          <div className="py-3">
            <div className="select-all break-all text-center text-[13px] font-semibold">{state.data.link}</div>
            <div className="mt-3 flex gap-2">
              <SmallButton
                onClick={act(async () => {
                  await copyText(state.data.link);
                  setCopied(true);
                })}
              >
                {copied ? t('shareCopied') : t('shareCopy')}
              </SmallButton>
              <SmallButton variant="accent" onClick={act(() => shareLink(state.data.link, t('refShareText')))}>
                {t('shareSend')}
              </SmallButton>
            </div>
            {error !== null ? <ErrorState error={error} withTitle={false} /> : null}
            <div className="mt-2">
              <ValueRow label={t('refInvited')} value={state.data.invited} />
              <ValueRow label={t('refDays')} value={state.data.bonusDays} />
            </div>
          </div>
        ) : null}
      </Card>
      <Hint>
        {t('refRule', {
          solo: REFERRAL.rewardDaysSolo,
          team: REFERRAL.rewardDaysTeam,
          max: state.status === 'ready' ? state.data.maxRewarded : REFERRAL.maxRewardedInvites,
        })}
      </Hint>
    </div>
  );
}
